import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { apiError } from '../lib/host';
import AuthShell from '../components/AuthShell';
import './Auth.css';

// Google reCAPTCHA v2 ("I'm not a robot"), loaded only when the server has a site key. The server checks the token
// with Google before creating anything (auth_service/captcha.py); the checkbox alone proves nothing.
let recaptchaLoading = null;
function loadRecaptcha() {
  if (window.grecaptcha?.render) return Promise.resolve(window.grecaptcha);
  if (!recaptchaLoading) {
    recaptchaLoading = new Promise((resolve, reject) => {
      window.__tcxRecaptchaReady = () => resolve(window.grecaptcha);
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?onload=__tcxRecaptchaReady&render=explicit';
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        recaptchaLoading = null;
        reject(new Error('reCAPTCHA did not load'));
      };
      document.head.appendChild(script);
    });
  }
  return recaptchaLoading;
}

/** The checkbox. `onToken` gets the token when ticked, and '' when it expires or errors (tick it again). */
function Recaptcha({ siteKey, onToken, widgetRef, onLoadError }) {
  const box = useRef(null);
  useEffect(() => {
    let gone = false;
    loadRecaptcha()
      .then((grecaptcha) => {
        if (gone || !box.current || widgetRef.current !== null) return;
        widgetRef.current = grecaptcha.render(box.current, {
          sitekey: siteKey,
          callback: (token) => onToken(token),
          'expired-callback': () => onToken(''),
          'error-callback': () => onToken(''),
        });
      })
      .catch(() => !gone && onLoadError());
    return () => {
      gone = true;
    };
  }, [siteKey, onToken, widgetRef, onLoadError]);
  return <div className="recaptcha-box" ref={box} />;
}

export default function Register() {
  const { register } = useAuth();
  const EMPTY = { name: '', email: '', phone: '', org_name: '' };
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  // Website Control (Super Admin): sign-up on or off. The server refuses sign-ups when off; this only explains it.
  const [site, setSite] = useState(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaWidget = useRef(null);
  const captchaKey = site?.recaptcha_site_key || '';

  useEffect(() => {
    authAPI.website().then((res) => setSite(res.data)).catch(() => {});
  }, []);

  const set = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    setError('');
    setSuccess('');
  };

  const onCaptcha = useCallback((token) => {
    setCaptchaToken(token);
    if (token) setError('');
  }, []);
  const onCaptchaLoadError = useCallback(() => setError("The \"I'm not a robot\" check couldn't load. Please check your connection and reload the page."), []);
  // A token is good for one attempt: after any failed sign-up the person ticks the box again.
  const resetCaptcha = () => {
    setCaptchaToken('');
    if (captchaWidget.current !== null) window.grecaptcha?.reset(captchaWidget.current);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (captchaKey && !captchaToken) return setError("Please confirm you're not a robot.");
    setLoading(true);
    setSuccess('');
    try {
      // The server creates the company and emails its login URL and a link to create the password.
      const data = await register(captchaKey ? { ...form, captcha_token: captchaToken } : form);
      setSuccess(data.message);
      setForm(EMPTY);
    } catch (err) {
      setError(apiError(err, 'Registration failed. Please try again.'));
    } finally {
      if (captchaKey) resetCaptcha(); // a token is good for one sign-up either way
      setLoading(false);
    }
  };

  const field = (name, label, type, placeholder) => (
    <div className="form-group">
      <label htmlFor={name}>{label}</label>
      <input type={type} id={name} name={name} value={form[name]} placeholder={placeholder} onChange={(e) => set(name, e.target.value)} required disabled={loading} />
    </div>
  );

  if (site && !site.registration_enabled) {
    return (
      <AuthShell side="register">
        <h2>Sign-up is closed for now.</h2>
        <p className="tcx-sub">New company registrations are turned off at the moment. Please check back later, or contact support@telecalx.com.</p>
        {site.login_enabled && (
          <p className="auth-link">
            Already have an account? <Link to="/login">Login</Link>
          </p>
        )}
      </AuthShell>
    );
  }

  return (
    <AuthShell side="register">
      <h2>Start with TeleCalX.</h2>
      <p className="tcx-sub">Create your company's account. After you verify your email, you'll choose your plan.</p>
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message" role="status">{success}</div>}

      <form onSubmit={handleSubmit}>
        {field('org_name', 'Company name', 'text', 'e.g. Acme Technologies')}
        {field('name', 'Your name', 'text', 'e.g. John Doe')}
        {field('email', 'Work email', 'email', 'e.g. john@acme.com')}
        {field('phone', 'Phone number', 'tel', 'e.g. 9876543210')}

        {captchaKey && <Recaptcha siteKey={captchaKey} onToken={onCaptcha} widgetRef={captchaWidget} onLoadError={onCaptchaLoadError} />}

        <button type="submit" disabled={loading} className="submit-btn">
          {loading ? 'Creating...' : 'Create company'}
        </button>
      </form>

      <p className="auth-link">
        Already have an account? <Link to="/login">Login</Link>
      </p>
    </AuthShell>
  );
}
